<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useStoresStore } from '@/entities/store'
import { usePermissions } from '@/shared/composables/usePermissions'
import { FEATURES } from '@/shared/config/permissions'
import { getCookie, setCookie } from '@/shared/lib/utils'

interface Emit {
  (event: 'addStore'): void
  (event: 'storeCreated', storeId: string): void
}

const emit = defineEmits<Emit>()

const storeStore = useStoresStore()
const { hasAccess } = usePermissions()

const { stores, isLoading } = storeToRefs(storeStore)

// Check if user can add more stores
// Essential users can only have 1 store, Premium users can have unlimited
const canAddMoreStores = computed(() => {
  // If no stores yet, always allow adding
  if (stores.value.length === 0) 
    return true
  // If Premium, allow unlimited stores
  return hasAccess(FEATURES.MULTI_STORE)
})

const defaultStoreId = getCookie('storeId')

// Ensure component is mounted and DOM is ready before rendering ElSelect to avoid teleport errors
const isMounted = ref(false)

onMounted(async () => {
  // Wait for next tick to ensure DOM is fully ready and Element Plus can create teleport containers
  await nextTick()
  isMounted.value = true
})

// Internal store selector logic
const _currentStoreId = ref<string | null>(defaultStoreId)

// Computed property with custom setter to control value changes
const currentStoreId = computed({
  get: () => _currentStoreId.value,
  set: (value: string | null) => {
    if (value === 'add-store') {
      // Don't update the value, just emit the event
      emit('addStore')
      return
    }
    // For normal store selection, update the value
    _currentStoreId.value = value
    if (value) {
      setCookie('storeId', value)
      window.location.reload()
    }
  },
})

// Method to update store when a new one is created
function updateToNewStore(newStoreId: string) {
  _currentStoreId.value = newStoreId
}

// Expose the method for parent component
defineExpose({
  updateToNewStore,
})
</script>

<template>
  <div class="relative space-y-3 mb-2">
    <div
      v-if="!stores.length"
      class="flex items-center gap-2 cursor-pointer bg-bg-secondary rounded-xl py-2.5 px-4 text-brand-violet"
      @click="$emit('addStore')"
    >
      <i class="icon-plus-circle text-2xl" />
      <span class="font-semibold text-[13px] leading-[20px]">
        {{ $t("add_store") }}
      </span>
    </div>

    <ElSelect
      v-if="isMounted && stores.length"
      v-model="currentStoreId"
      :placeholder="
        stores.length
          ? $t('select_store_placeholder')
          : $t('add_your_first_store')
      "
      :disabled="isLoading"
      size="large"
      class="w-full !border-none !h-11"
      :teleported="false"
    >
      <template #prefix>
        <img
          src="@/shared/assets/svg/wb-icon.svg"
          class="w-5 h-5 object-contain"
        >
      </template>

      <!-- Store options -->
      <ElOption
        v-for="store in stores"
        :key="store.id"
        :label="store.name"
        :value="store.id.toString()"
        class="!py-2 !px-3 hover:!bg-bg-secondary"
      >
        <div class="flex items-center gap-3">
          <img
            src="@/shared/assets/svg/wb-icon.svg"
            class="w-5 h-5 object-contain"
            :alt="store.name"
          >

          <span class="text-sm text-text-primary">{{ store.name }}</span>
        </div>
      </ElOption>

      <!-- Add Store Option (only for Premium or users with no stores) -->
      <ElOption
        v-if="canAddMoreStores"
        value="add-store"
        class="!py-2 !px-3 hover:!bg-bg-secondary !text-primary"
      >
        <div class="flex items-center gap-3 text-primary">
          <i class="icon-plus-circle text-base" />
          <span class="text-sm font-medium">{{ $t("add_store") }}</span>
        </div>
      </ElOption>
    </ElSelect>
  </div>
</template>

<style scoped>
:deep(.el-select__wrapper) {
  box-shadow: none !important;
  height: 44px !important;
}

@media screen and (max-width: 768px) {
  :deep(.el-select--large .el-select__wrapper) {
    padding: 0 10px !important;
    height: 36px !important;
  }
}
</style>
